import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    DOCUMENT,
    ElementRef,
    HostListener,
    inject,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    signal
} from '@angular/core';
import {SessionViewModel} from '../ts/session-view-model';
import {SessionProviderService} from '../session-provider.service';
import {SessionStoreService} from '../session-store.service';
import {RdpClientUIPropertiesService} from '../rdp-client-uiproperties.service';
import {LoggerService} from '../logger.service';
import {PlatformInfoService} from '../platform-info.service';
import {UserAuthInfoService} from '../user-auth-info.service';
import {Bookmark} from '../ts/bookmark';
import {BookmarkType} from '../ts/enum/bookmark-type';
import {SessionProviderEvent} from '../ts/enum/session-provider-event';
import {Redirections} from '../ts/redirections';
import {TelemetryService} from '../telemetry.service';
import {FeedData} from '../ts/feed-data';
import {Session} from '../ts/session';
import {RdpConnection} from '../ts/rdp-connection';
import {ObjectHelper} from '../ts/object-helper';
import {ADDomain, ADUserHtml5, DesktopServer, Guac, RemoteAppHtml5} from '../../models';
import {StompConnectionStatus} from '../../ws/stomp-connection-status.enum';
import {RemoteAppDaoService} from '../../main/remote-app/remote-app-dao.service';
import {BehaviorSubject, forkJoin, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, filter, first, shareReplay, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AdUserDaoService} from '../../main/ad-user/ad-user-dao.service';
import {DesktopServerDaoService} from '../../main/desktop-server/desktop-server-dao.service';
import {AdDomainDaoService} from '../../main/ad-domain/ad-domain-dao.service';
import {CredentialRequest} from '../ts/credential-request';
import {FuseConfigService} from '../../../../@fuse/services/config.service';
import {AppDrawerComponent} from './app-drawer/app-drawer.component';
import {SessionStateModalComponent} from './session-state-modal/session-state-modal.component';
import {SessionStateStoreService} from '../session-state-store.service';
import {locale as ptBr} from '../i18n/pt-br';
import {FuseTranslationLoaderService} from '../../../../@fuse/services/translation-loader.service';
import {R2CloudClientService} from '../../ws/r2-cloud-client.service';
import {debounce} from '../../helpers/utils';
import {Router} from '@angular/router';
import {AESEncryptor} from '../ts/decrypt';
import {RemoteAppHtml5Service} from '../remote-app-html5.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../helpers/confirm-dialog/confirm-dialog.component';
import {DownloadFileService} from '../../main/my-docs/download-file.service';
import {R2CloudStompService} from '../../ws/r2-cloud-stomp.service';

import {AdUserPoolDaoService} from '../../main/ad-user-pool/ad-user-pool-dao.service';
import {GuacDaoService} from '../../main/guac/guac-dao.service';
import {ContingencyService} from '../../main/login/contingency.service';


@Component({
    selector: 'app-session-view',
    templateUrl: './session-view.component.html',
    styleUrls: ['./session-view.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SessionViewComponent extends SessionViewModel implements OnInit, OnDestroy {
    private static readonly VIRTUAL_KEYBOARD_STORAGE_KEY = 'r2cloud-html5-virtual-keyboard';
    private readonly destroyRef = inject(DestroyRef);
    private canvasContainerId = 'canvas-container';

    constructor(private router: Router,
                @Inject(DOCUMENT) private document: Document,
                elementRef: ElementRef,
                renderer: Renderer2,
                sessionProvider: SessionProviderService,
                sessionStore: SessionStoreService,
                rdpClientUIProperties: RdpClientUIPropertiesService,
                logger: LoggerService,
                platformInfo: PlatformInfoService,
                userAuthInfo: UserAuthInfoService,
                telemetry: TelemetryService,
                sessionStateStore: SessionStateStoreService,
                private stomp: R2CloudStompService,
                private guacDaoService: GuacDaoService,
                private remoteAppDao: RemoteAppDaoService,
                private adUserDao: AdUserDaoService,
                private adUserPoolDaoService: AdUserPoolDaoService,
                private desktopServerDao: DesktopServerDaoService,
                private adDomainDao: AdDomainDaoService,
                private fuseConfig: FuseConfigService,
                private dialog: MatDialog,
                private fuseTranslationLoaderService: FuseTranslationLoaderService,
                private clientService: R2CloudClientService,
                private remoteAppHtml5Service: RemoteAppHtml5Service,
                private contingencyService: ContingencyService,
                private downloadFileService: DownloadFileService,
                private cdr: ChangeDetectorRef) {
        super(sessionProvider, sessionStore, rdpClientUIProperties, logger, platformInfo, () => this.createCanvas(), userAuthInfo, sessionStateStore);
        this.showR2CloudRobot = this.document.location.href.indexOf('r2cloud') > -1 || this.document.location.href.indexOf('kluh') > -1;

        window.addEventListener('click', this.resumeAudioContext);
        this.clientService.disable();
        this.fuseTranslationLoaderService.loadTranslations(ptBr);
        fuseConfig.setConfig({
            layout: {
                navbar: {
                    hidden: true
                },
                toolbar: {
                    hidden: true
                }
            }
        });
        this.stompConnectionSubscription = this.stomp.connectionStatus$
            .pipe(takeUntil(this.destroy$))
            .subscribe((status: StompConnectionStatus) => {
                this.stompConnectionStatus = status;

                // Mostra um botão de "reconectar" apenas após X segundos desconectado
                if (status === StompConnectionStatus.DISCONNECTED && !this.showReconnectButton) {
                    if (this.waitingTimerForButton === 10) {
                        clearInterval(this.timerForButton);
                        this.timerForButton = setInterval(() => {
                            this.waitingTimerForButton--;
                            if (this.waitingTimerForButton < 1) {
                                clearInterval(this.timerForButton);
                                if (this.stompConnectionStatus === StompConnectionStatus.DISCONNECTED && !this.showReconnectButton) {
                                    this.showReconnectButton = true;
                                }
                            }
                            this.cdr.markForCheck();
                        }, 1000);
                    }
                }

                if (status === StompConnectionStatus.OK) {
                    localStorage.setItem('webClientId', this.stomp.webClientId);
                    this.encryptor = new AESEncryptor(this.stomp.webClientId);

                    clearInterval(this.timerForButton);
                    this.showReconnectButton = false;
                    this.waitingTimerForButton = 10;
                }
            });


        this.remoteAppsHtml5Subscription = this.remoteAppHtml5Service.remoteAppChanges().subscribe((remoteAppHtml5) => {
            const index = this.remoteAppHtml5List.findIndex((value) => value.remoteAppId === remoteAppHtml5.remoteAppId);
            if (index > -1) {
                this.remoteAppHtml5List[index] = remoteAppHtml5;
            } else {
                this.remoteAppHtml5List.push(remoteAppHtml5);
            }
        });

        this.sessionProvider = sessionProvider;
        this.elementRef = elementRef;
        this.telemetry = telemetry;
        this.sessionView = this;
        this.renderer = renderer;
        this.initDelegation();

    }

    private renderer!: Renderer2;
    private stompConnectionStatus!: StompConnectionStatus;
    private showReconnectButton!: boolean;
    private waitingTimerForButton = 10;
    private timerForButton: any;
    private encryptor!: AESEncryptor;
    private remoteAppsHtml5Subscription!: Subscription;
    private stompConnectionSubscription!: Subscription;
    private remoteAppHtml5List: RemoteAppHtml5[] = [];
    private adUserList: ADUserHtml5[] = [];
    private readonly destroy$ = new Subject<void>();
    private desktopServerList: DesktopServer[] = [];
    private adDomainList: ADDomain[] = [];
    private sessionStateModalDict: { [key: string]: any } = {};
    showR2CloudRobot = false;
    readonly virtualKeyboardVisible = signal(localStorage.getItem(SessionViewComponent.VIRTUAL_KEYBOARD_STORAGE_KEY) === '1');
    readonly isGuacActive = signal(false);
    private elementRef!: ElementRef;
    private telemetry!: TelemetryService;
    sessionView: any;
    objectHelper = new ObjectHelper();
    loading = true;
    @Output()
    displayImageBackground = new BehaviorSubject(true);

    private i: { width: number | null; height: number | null } = {
        width: null,
        height: null
    };

    openAppDrawerDebounced = debounce(() => {
        this.loading = false;
        this.cdr.markForCheck();
        this.openAppDrawer();
    }, 2000);

    onResized = debounce((doc: any) => {
        const canvasContainer = doc.getElementById(this.canvasContainerId) as HTMLElement | null;
        if (!canvasContainer) { return; }

        const width = canvasContainer.offsetWidth;
        const height = canvasContainer.offsetHeight;

        if (this.i.width !== width || this.i.height !== height) {
            this.sessionProvider.connectionList().forEach(conn => conn.updateMonitorLayout());
            this.i.width = width;
            this.i.height = height;
        }
    }, 300);

    resumeAudioContext = (): void => {
        const audioContext = this.sessionProvider.audioContext;
        if (!audioContext) { return; }

        if (audioContext.state === 'suspended') {
            audioContext.resume()
                .finally(() => window.removeEventListener('click', this.resumeAudioContext));
            return;
        }

        window.removeEventListener('click', this.resumeAudioContext);
    };

    onRedirectionsRequest = (a: any) => {
        if (a) {
            const g = new Redirections();
            g.clipboard = true;
            g.printer = true;
            g.rememberCollection = false;
            a.complete(g);
        }
    };

    @HostListener('window:resize')
    onResize(): void {
        this.onResized(this.document);
    }

    private updateDimensions(): void {
        this.i.width = this.elementRef.nativeElement.offsetWidth;
        this.i.height = this.elementRef.nativeElement.offsetHeight;
    }

    private initDelegation(): void {
        this.delegate.onCredentialsNeeded = (request: CredentialRequest, connection: RdpConnection): void => {
            if (!request) { return; }

            if (!connection) {
                this.logger.error('[SessionView] onCredentialsNeeded: connection is undefined/null');
                return;
            }

            // garante encryptor (inclui modo offline)
            if (!this.encryptor) {
                const webClientId = this.stomp?.webClientId || localStorage.getItem('webClientId');
                if (webClientId) {
                    this.encryptor = new AESEncryptor(webClientId);
                }
            }

            const remoteAppId = connection.remoteAppId;
            const remoteApp = this.remoteAppHtml5List?.find(x => x.remoteAppId === remoteAppId);
            if (!remoteApp) {
                this.logger.error('[SessionView] RemoteApp não encontrado', { remoteAppId, connectionId: connection.id });
                return;
            }

            const desktopServer =
                this.desktopServerList?.find(x => x.id === remoteApp.desktopServerId) ||
                this.desktopServerList?.[0];

            if (!desktopServer) {
                this.logger.error('[SessionView] DesktopServer não encontrado para RemoteApp', {
                    remoteAppId,
                    desktopServerId: remoteApp.desktopServerId
                });
                return;
            }

            const adDomain = this.adDomainList?.find(x => desktopServer.adDomainId === x.id);
            if (!adDomain) {
                this.logger.error('[SessionView] ADDomain não encontrado para DesktopServer', {
                    remoteAppId,
                    desktopServerId: desktopServer.id,
                    adDomainId: desktopServer.adDomainId
                });
                return;
            }

            const cachedAdUser = this.adUserList?.find(x => x.adDomainId === adDomain.id);

            const adUser$ = cachedAdUser
                ? of(cachedAdUser)
                : this.adUserDao.getOneHtml5(remoteApp.adUserId ?? 0).pipe(
                    take(1),
                    tap(u => {
                        if (u) {
                            this.adUserList.push(u);
                        }
                    }),
                    catchError(err => {
                        this.logger.error('[SessionView] Falha ao buscar ADUserHtml5', { adUserId: remoteApp.adUserId, err });
                        return of(null);
                    })
                );

            adUser$.pipe(take(1)).subscribe((adUser: ADUserHtml5 | null) => {
                if (!adUser) {
                    this.logger.error('[SessionView] ADUserHtml5 ausente, não foi possível montar credenciais', {
                        remoteAppId,
                        adUserId: remoteApp.adUserId
                    });
                    return;
                }

                if (!this.encryptor) {
                    this.logger.error('[SessionView] Encryptor indefinido. Sem webClientId para decriptar credenciais', {
                        remoteAppId,
                        webClientId: this.stomp?.webClientId || localStorage.getItem('webClientId')
                    });
                    return;
                }

                this.encryptor
                    .decrypt(adUser.login ?? '', adDomain.domainName ?? '', adUser.password ?? '', request.getOriginal())
                    .catch(err => this.logger.error('[SessionView] decrypt falhou', { remoteAppId, err }));
            });
        };










        this.delegate.onTrustChallenge = (a): void => {
            if (a) {
                a.complete(1);
            }
        };
        this.sessionProvider.events.subscribe(SessionProviderEvent.RedirectionsRequest, this.onRedirectionsRequest);
        this.delegate.openLoaderModal = (sessionId: string, connectionId: string, _stateDict: any, _sessionLabel: string) => {
            this.sessionStateModalDict[sessionId] = this.dialog.open(SessionStateModalComponent, {
                disableClose: true,
                panelClass: 'session-state-modal',
                data: {
                    sessionId: sessionId,
                    connectionId: connectionId,
                }
            });
        };
        this.delegate.closeLoaderModal = (sessionId: string) => {
            this.displayImageBackground.next(false);
            const modal: MatDialogRef<SessionStateModalComponent> = this.sessionStateModalDict[sessionId];
            if (modal) {
                modal.close();
            }
        };
        this.delegate.allSessionsDisconnected = () => {
            const canvasContainer = document.getElementById('canvas-container') as HTMLDivElement;
            if (canvasContainer) {
                canvasContainer.childNodes.forEach((node) => {
                    (node as HTMLElement).style.display = 'none';
                });
                this.openAppDrawerDebounced();
            }
            this.displayImageBackground.next(true);
        };
        const feedData = new FeedData();
        feedData.loading = false;
        this.telemetry.onFeedsLoaded(feedData);
    }

    createCanvas(): HTMLCanvasElement {
        const canvasContainer = this.document.getElementById(this.canvasContainerId) as HTMLDivElement | null;
        const canvas = this.document.createElement('canvas') as HTMLCanvasElement;
        canvas.id = `shown-canvas-${new ObjectHelper().createGuid()}`;

        if (!canvasContainer) {
            this.logger.error('[SessionView] canvas container não encontrado', { canvasContainerId: this.canvasContainerId });
            return canvas;
        }

        this.renderer.appendChild(canvasContainer, canvas);

        canvas.addEventListener('mousedown', (ev: MouseEvent) => {
            this.mouseDown(ev.pageX, ev.pageY, ev.which);
            ev.preventDefault();
        });

        canvas.addEventListener('mouseup', (ev: MouseEvent) => {
            this.mouseUp(ev.pageX, ev.pageY, ev.which);
            ev.preventDefault();
        });

        canvas.addEventListener('mousemove', (ev: MouseEvent) => {
            this.mouseMove(ev.pageX, ev.pageY);
            ev.preventDefault();
        });

        canvas.addEventListener('mouseleave', (ev: MouseEvent) => this.mouseLeave(ev.pageX, ev.pageY));
        canvas.addEventListener('mouseenter', () => this.mouseEnter());

        canvas.addEventListener('wheel', (ev: WheelEvent) => {
            this.mouseWheel(-ev.deltaX, -ev.deltaY);
            ev.preventDefault();
        });

        canvas.addEventListener('contextmenu', ev => ev.preventDefault());

        canvas.addEventListener('touchstart', (ev: TouchEvent) => this.touchStart(ev.changedTouches));
        canvas.addEventListener('touchmove', (ev: TouchEvent) => this.touchMove(ev.changedTouches));
        canvas.addEventListener('touchend', (ev: TouchEvent) => this.touchEnd(ev.changedTouches));
        canvas.addEventListener('touchcancel', (ev: TouchEvent) => this.touchCancel(ev.changedTouches));

        return canvas;
    }

    ngOnInit(): void {
        this.init();
        this.openAppDrawerDebounced();
        this.updateDimensions();
    }

    ngOnDestroy(): void {
        this.downloadFileService.stompUnsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
        this.sessionProvider.events.unsubscribe(SessionProviderEvent.RedirectionsRequest, this.onRedirectionsRequest);
        window.removeEventListener('click', this.resumeAudioContext);
        this.clientService.enable();
        this.fuseConfig.setConfig({
            layout: {
                navbar: {
                    hidden: false
                },
                toolbar: {
                    hidden: false
                }
            }
        });
        this.stompConnectionSubscription?.unsubscribe();
        this.remoteAppsHtml5Subscription?.unsubscribe();
        this.disconnectAllSessions();
        this.openAppDrawerDebounced.cancel();
        this.removeGuacFocus();
    }

    onRemoteAppHtml5Open(remoteAppHtml5: RemoteAppHtml5): void {
        const cachedAdUser = this.adUserList.find(x => x.adUserId === remoteAppHtml5.adUserId);
        const cachedDesktopServer = this.desktopServerList.find(x => x.id === remoteAppHtml5.desktopServerId);

        const adUser$: Observable<ADUserHtml5 | null> = cachedAdUser
            ? of(cachedAdUser)
            : (remoteAppHtml5.poolUser
                    ? this.adUserPoolDaoService.getOneHtml5(remoteAppHtml5.adUserId ?? 0)
                    : this.adUserDao.getOneHtml5(remoteAppHtml5.adUserId ?? 0)
            ).pipe(
                take(1),
                tap(result => {
                    if (result) {
                        this.adUserList.push(result);
                    }
                }),
                catchError(err => {
                    this.logger.error('[SessionView] Falha ao buscar ADUserHtml5', { adUserId: remoteAppHtml5.adUserId, err });
                    return of(null);
                }),
                shareReplay(1)
            );

        const desktopServer$: Observable<DesktopServer | null> = (cachedDesktopServer && !cachedDesktopServer.desktopServerPoolId)
            ? of(cachedDesktopServer)
            : this.desktopServerDao.getRandomDesktopServerByDesktopServerId(remoteAppHtml5).pipe(
                take(1),
                tap(result => {
                    if (result) {
                        this.desktopServerList.push(result);
                    }
                }),
                catchError(err => {
                    this.logger.error('[SessionView] Falha ao buscar DesktopServer', { remoteAppId: remoteAppHtml5.remoteAppId, err });
                    return of(null);
                })
            );

        forkJoin([adUser$, desktopServer$])
            .pipe(
                switchMap(([adUser, desktopServer]) => {
                    if (!desktopServer) {
                        return of<[ADUserHtml5 | null, DesktopServer | null, ADDomain | null]>([adUser, null, null]);
                    }

                    const cachedDomain = this.adDomainList.find(x => x.id === desktopServer.adDomainId);
                    const adDomain$: Observable<ADDomain | null> = cachedDomain
                        ? of(cachedDomain)
                        : this.adDomainDao.getOne(desktopServer.adDomainId ?? 0).pipe(
                            take(1),
                            tap(result => {
                                if (result) {
                                    this.adDomainList.push(result);
                                }
                            }),
                            catchError(err => {
                                this.logger.error('[SessionView] Falha ao buscar ADDomain', { adDomainId: desktopServer.adDomainId, err });
                                return of(null);
                            })
                        );

                    return forkJoin([of(adUser), of(desktopServer), adDomain$]);
                }),
                takeUntil(this.destroy$),
                tap(([_adUser, desktopServer, adDomain]) => {
                    if (!desktopServer || !adDomain) {
                        this.logger.error('[SessionView] Dados insuficientes para abrir RemoteApp', {
                            remoteAppId: remoteAppHtml5.remoteAppId,
                            hasDesktopServer: !!desktopServer,
                            hasAdDomain: !!adDomain
                        });
                        return;
                    }

                    if (remoteAppHtml5.app?.toLowerCase() === 'serviceapp') {
                        this.openGM(remoteAppHtml5);
                    } else {
                        this.processClick(remoteAppHtml5, desktopServer, adDomain);
                    }
                }),
                filter(([_, desktopServer, adDomain]) => !!desktopServer && !!adDomain),
                switchMap(() => {
                    if (!this.contingencyService.isEnabled()) {
                        return this.remoteAppDao.markAsOpenInHTML5(remoteAppHtml5)
                            .pipe(take(1), catchError(err => {
                                this.logger.error('[SessionView] Falha ao marcar RemoteApp como aberto no HTML5', { remoteAppId: remoteAppHtml5.remoteAppId, err });
                                return of(null);
                            }));
                    }
                    return of(null);
                })
            )
            .subscribe();
    }

    private openGM(remoteAppHtml5: RemoteAppHtml5): void {
        this.removeIframeGM();
        this.hideChildCanvas();

        if (this.contingencyService.isEnabled()) {
            this.openGMContingency(remoteAppHtml5);
            return;
        }

        this.guacDaoService.getToken(remoteAppHtml5).subscribe({
            next: (guac) => {
                if (guac) {
                    this.cacheGuacContingency(remoteAppHtml5.remoteAppId, guac);
                    this.createGuacIframe('https://' + guac.fqdn + ':' + guac.port + '/guacamole/?token=' + guac.token);
                }
            },
            error: (err) => {
                this.logger.error('[SessionView] Falha ao obter token Guacamole', { remoteAppId: remoteAppHtml5.remoteAppId, err });
                this.showChildCanvas();
            }
        });
    }

    private openGMContingency(remoteAppHtml5: RemoteAppHtml5): void {
        const cached = this.getCachedGuacContingency(remoteAppHtml5.remoteAppId);
        if (!cached) {
            this.dialog.open(ConfirmDialogComponent, {
                disableClose: true,
                data: {
                    message: 'Não há dados de contingência para este app de serviço.<br />Abra-o pelo menos uma vez no modo normal.',
                    disableCancel: true,
                    confirmButtonValue: 'OK',
                    icon: 'error_outline'
                }
            });
            this.showChildCanvas();
            return;
        }

        const guacUrl = `https://${cached.fqdn}:${cached.port}/guacamole/?data=${encodeURIComponent(cached.contingencyPayload)}`;
        this.createGuacIframe(guacUrl);
    }

    private createGuacIframe(url: string): void {
        this.isGuacActive.set(true);
        const canvasContainer = document.getElementById(this.canvasContainerId) as HTMLElement | null;
        const iframe = document.createElement('iframe');
        iframe.id = 'iframe-gm';
        iframe.src = url;
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.margin = '0';
        iframe.style.padding = '0';
        iframe.style.border = '0';
        iframe.allowFullscreen = true;
        iframe.setAttribute('allow', 'clipboard-read; clipboard-write');
        canvasContainer?.appendChild(iframe);
        this.toggleGuacamoleFocus();
    }

    private cacheGuacContingency(remoteAppId: number | null, guac: Guac): void {
        if (remoteAppId == null || !guac.contingencyPayload) {
            return;
        }
        try {
            localStorage.setItem('guac-contingency-' + remoteAppId, JSON.stringify({
                contingencyPayload: guac.contingencyPayload,
                fqdn: guac.fqdn,
                port: guac.port,
                path: guac.path
            }));
        } catch (e) {
            this.logger.error('[SessionView] Falha ao cachear dados de contingência Guacamole', e);
        }
    }

    private getCachedGuacContingency(remoteAppId: number | null): { contingencyPayload: string; fqdn: string; port: number; path: string | null } | null {
        if (remoteAppId == null) {
            return null;
        }
        try {
            const cached = localStorage.getItem('guac-contingency-' + remoteAppId);
            return cached ? JSON.parse(cached) : null;
        } catch (e) {
            return null;
        }
    }

    private refocusGuacamole(): void {
        const iframe = document.getElementById('iframe-gm');
        if (iframe && document.activeElement !== iframe) {
            iframe.focus();
        }
    }

    private toggleGuacamoleFocus(): void {
        this.removeGuacFocus();
        this.addGuacFocus();
    }

    private removeGuacFocus(): void {
        document.removeEventListener('click', this.refocusGuacamole);
        document.removeEventListener('keydown', this.refocusGuacamole);
    }

    private addGuacFocus(): void {
        document.addEventListener('click', this.refocusGuacamole);
        document.addEventListener('keydown', this.refocusGuacamole);
    }

    private getCanvasContainer(): HTMLElement | null {
        return this.document.getElementById(this.canvasContainerId);
    }

    removeChildById(childId: string): void {
        const parentEl = this.getCanvasContainer();
        const childEl = childId && this.document.getElementById(childId);
        if (parentEl && childEl) {
            this.renderer.removeChild(parentEl, childEl);
        }
    }

    setCanvasDisplay(displayValue: 'none' | 'block'): void {
        const parentEl = this.getCanvasContainer();
        if (!parentEl) {
            return;
        }
        const canvases = Array.from(parentEl.querySelectorAll('canvas')) as HTMLElement[];
        canvases.forEach(canvasEl => {
            this.renderer.setStyle(canvasEl, 'display', displayValue);
        });
    }

    private removeIframeGM(): void {
        this.isGuacActive.set(false);
        this.removeChildById('iframe-gm');
    }

    private hideChildCanvas(): void {
        this.setCanvasDisplay('none');
    }

    private showChildCanvas(): void {
        this.setCanvasDisplay('block');
    }

    onSessionClick(session: Session): void {
        this.removeGuacFocus();
        this.removeIframeGM();
        this.showChildCanvas();
        this.focusToSession(session.id);
    }

    private processClick(remoteAppHtml5: RemoteAppHtml5, desktopServer: DesktopServer, adDomain: ADDomain): void {
        this.removeGuacFocus();
        this.removeIframeGM();
        this.showChildCanvas();
        if (!desktopServer.gatewayFQDN) {
            this.dialog.open(ConfirmDialogComponent, {
                disableClose: true,
                data: {
                    message: 'Esse servidor não tem <b>Gateway FQDN</b> cadastrado.<br />Entre em contato com o suporte.',
                    disableCancel: true,
                    confirmButtonValue: 'OK',
                    icon: 'error_outline'
                }
            }).afterClosed().pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => {
                this.openAppDrawer();
            });

        } else {
            let fullDesktopServerAddress = (desktopServer.hostname ?? '').toLowerCase() + '.' + (adDomain.domainName ?? '').toLowerCase();
            if (desktopServer.desktopServerPoolId && desktopServer.disposable && desktopServer.adIp) {
                fullDesktopServerAddress = desktopServer.adIp;
            }
            const bookmark = new Bookmark();
            bookmark.id = this.objectHelper.createGuid();
            bookmark.remoteAppId = remoteAppHtml5.remoteAppId ?? 0;
            bookmark.adminMode = false;
            bookmark.bookmarkType = BookmarkType.RemoteApp;
            bookmark.port = desktopServer.internalPort || 3389;
            bookmark.friendlyName = remoteAppHtml5.remoteAppId;
            bookmark.imageFileId = remoteAppHtml5.imageUUID ?? '';
            bookmark.workspaceName = fullDesktopServerAddress;
            bookmark.rdpFileString = `redirectclipboard:i:1
redirectprinters:i:1
devicestoredirect:s:*
session bpp:i:32
remoteapplicationmode:i:1
allow font smoothing:i:1
promptcredentialonce:i:1
gatewayusagemethod:i:1
gatewayprofileusagemethod:i:1
gatewaycredentialssource:i:0
full address:s:${fullDesktopServerAddress}
server port:i:${bookmark.port}
remoteapplicationprogram:s:${remoteAppHtml5.app}
gatewayhostname:s:${desktopServer.gatewayFQDN}
remoteapplicationname:s:${remoteAppHtml5.remoteAppId}
remoteapplicationcmdline:s:${remoteAppHtml5.appArgs || ''}
workspace id:s:${fullDesktopServerAddress}
use redirection server name:i:1`;
            this.sessionProvider.createSession(bookmark, false, remoteAppHtml5.remoteAppId ?? 0);
        }


    }

    openAppDrawer(): void {
        const dialogRef: MatDialogRef<AppDrawerComponent> = this.dialog.open(AppDrawerComponent, {
            disableClose: false,
            panelClass: 'generic-transparent-dialog-large',
        });
        dialogRef.afterClosed().pipe(first(), filter((x) => !!(x)), takeUntil(this.destroy$)).subscribe((remoteAppHtml5: RemoteAppHtml5) => {
            this.onRemoteAppHtml5Open(remoteAppHtml5);
        });
    }

    getSessionsStore(): any {
        return this.sessionList;
    }

    toggleVirtualKeyboard(): void {
        const next = !this.virtualKeyboardVisible();
        this.virtualKeyboardVisible.set(next);
        localStorage.setItem(SessionViewComponent.VIRTUAL_KEYBOARD_STORAGE_KEY, next ? '1' : '0');
    }

    goToHome(): void {
        if (this.isFullscreen()) {
            this.exitFullscreen();
        }
        window.location.href = '/desktop';
    }

    isFullscreen(): boolean {
        return !!document.fullscreenElement;
    }

    goFullscreen(): void {
        if (document.fullscreenEnabled) {
            document.documentElement.requestFullscreen();
        }
    }

    exitFullscreen(): void {
        document.exitFullscreen();
    }
}
